import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import moment from "moment";
import { generateClient } from "@aws-amplify/api";
import { getWorkflows } from "@/graphql/queries";

const client = generateClient();

const Stat = ({ value, label }) => (
  <div className="text-center px-3 py-2">
    <div style={{ fontSize: "1.5rem", fontWeight: 700 }}>{value ?? "N/A"}</div>
    <div className="text-muted" style={{ fontSize: ".9rem" }}>{label}</div>
  </div>
);

export default function FlowExpandedView() {
  const { id } = useParams();
  const [wf, setWf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const fetchWorkflow = useCallback(async () => {
    if (!id) return;
    try {
      setErr(null);
      const res = await client.graphql({
        query: getWorkflows,
        variables: {
          filters: [{ id: { eq: Number(id) } }],
          limit: 1,
          offset: 0,
          sorting: { field: "updated_at", order: "desc" },
        },
      });
      const data = res?.data?.getWorkflows?.workflows ?? [];
      const w = data[0] || null;
      if (w && typeof w.tasks_json === "string") {
        try {
          w.tasks_json = JSON.parse(w.tasks_json);
        } catch {}
      }
      setWf(w);
    } catch (e) {
      console.error(e);
      setErr("Failed to load workflow.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchWorkflow();
  }, [fetchWorkflow]);

  // Optional polling so the pill reflects changes made elsewhere
  useEffect(() => {
    const t = setInterval(fetchWorkflow, 15000);
    return () => clearInterval(t);
  }, [fetchWorkflow]);

  if (loading) return <div className="container-fluid pe-4 ps-5 pt-4">Loading…</div>;
  if (err) return <div className="container-fluid pe-4 ps-5 pt-4 text-danger">{err}</div>;
  if (!wf) return <div className="container-fluid pe-4 ps-5 pt-4">Workflow not found.</div>;

  const isActive = !!wf.is_active;

  return (
    <div className="container-fluid pe-4 ps-5 pt-4">
      {/* Title */}
      <div>
        <h3
          className="fw-semibold mb-0 ps-2"
          style={{ color: "#2E2868", fontSize: "1.4375rem", lineHeight: "1" }}
        >
          {wf.name || "Workflow"}
        </h3>
      </div>

      {/* Meta row + Buttons */}
      <div className="d-flex justify-content-between align-items-center mt-1 mb-4 ps-2">
        {/* Meta info */}
        <div className="text-muted" style={{ fontSize: ".95rem" }}>
          <span className="me-3">
            <strong>Date Created:</strong>{" "}
            {wf.created_at ? moment(wf.created_at).format("MM/DD/YY") : "N/A"}
          </span>
          <span className="me-3">
            <strong>Study:</strong> {wf.study_name || "N/A"}
          </span>
          <span className="me-3">
            <strong>Recruiting Source:</strong> {wf.recruitment_source_name || "N/A"}
          </span>
          <span className="me-2">
            <strong>Status:</strong>{" "}
            {isActive ? (
              <span className="badge bg-success">Active</span>
            ) : (
              <span className="badge bg-danger">Inactive</span>
            )}
          </span>
        </div>

        {/* Buttons */}
        <div className="d-flex gap-2">
          <Link
            to="/app/customers/workflows"
            className="btn btn-sm btn-outline-secondary"
            style={{
              backgroundColor: "#2E2868",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              padding: "6px 12px",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            }}
          >
            Back to Workflows
          </Link>

          <Link
            to={`/app/customers/workflows/${id}`}
            className="btn btn-sm btn-outline-secondary"
            style={{
              backgroundColor: "#2E2868",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              padding: "6px 12px",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            }}
          >
            Edit Workflow
          </Link>
        </div>
      </div>

      <hr />

      {/* Widgets row */}
      <div className="row g-4 mt-2 pt-2">
        {/* Left widget: KPIs */}
        <div className="col-12 col-lg-6">
          <div
            className="p-3 rounded-4 h-100"
            style={{ border: "3px solid #e5e7eb", boxShadow: "0 2px 6px rgba(0,0,0,0.06)" }}
          >
            <div className="row g-3">
              <div className="col-6">
                <Stat value={wf.total_participants} label="Total Participants" />
              </div>
              <div className="col-6">
                <Stat value={wf.completed_workflow} label="Completed Workflow" />
              </div>
              <div className="col-6">
                <Stat value={wf.scheduled_appointments} label="Scheduled Appointments" />
              </div>
              <div className="col-6">
                <Stat value={wf.completed_time || "N/A"} label="Completed Workflow" />
              </div>
              <div className="col-6">
                <Stat value={wf.drop_off_rate} label="Drop-Off Rate" />
              </div>
              <div className="col-6">
                <Stat value={wf.escalations} label="Escalations to Live Agent" />
              </div>
            </div>
          </div>
        </div>

        {/* Right widget: placeholder */}
        <div className="col-12 col-lg-6">
          <div
            className="d-flex align-items-center justify-content-center text-muted rounded-4 h-100"
            style={{
              minHeight: 180,
              border: "3px solid #e5e7eb",
              boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
            }}
          >
            <div className="text-center">
              <div style={{ fontWeight: 600 }}>AVG length of subject per step</div>
              <small className="d-block mt-1">N/A</small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
